import React from "react";
import classNames from "classnames";
import { CheckIcon } from "@icons";
import { BackgroundColorPicker } from "./BackgroundColorPicker";
import styles from "./DesignSection.module.scss";

interface SwatchTileProps {
    label: string;
    // The swatch fill. Omit for the printer-friendly (white paper) tile, which
    // renders its own chrome instead of a flat fill.
    color?: string;
    selected: boolean;
    onClick: () => void;
    // Dark swatches (the Font color presets) need the light check variant so
    // the mark stays visible against the fill.
    isCheckOnDark?: boolean;
}

export const SwatchTile: React.FC<SwatchTileProps> = ({
    label,
    color,
    selected,
    onClick,
    isCheckOnDark = false,
}) => (
    <button
        type="button"
        aria-label={label}
        title={label}
        className={classNames(styles.swatch, {
            [styles.printerSwatch]: color === undefined,
            [styles.swatchSelected]: selected,
        })}
        style={color !== undefined ? { backgroundColor: color } : undefined}
        onClick={onClick}
    >
        {selected && (
            <CheckIcon
                className={classNames(styles.swatchCheck, {
                    [styles.swatchCheckOnDark]: isCheckOnDark,
                })}
            />
        )}
    </button>
);

interface CustomColorTileProps {
    value: string;
    onChange: (color: string) => void;
    // Distinct accessible name per section so screen-reader users can tell the
    // background-color trigger apart from the font-color trigger — the two
    // tiles are otherwise identical.
    label: string;
    selected: boolean;
    isCheckOnDark?: boolean;
}

// Last tile in a swatch grid: the color wheel reads as "any color" until one is
// picked, then it behaves like the presets.
export const CustomColorTile: React.FC<CustomColorTileProps> = ({
    value,
    onChange,
    label,
    selected,
    isCheckOnDark = false,
}) => (
    <BackgroundColorPicker value={value} onChange={onChange} ariaLabel={label}>
        <button
            type="button"
            aria-label={label}
            title={label}
            className={classNames(styles.swatch, styles.customSwatch, {
                [styles.swatchSelected]: selected,
            })}
            style={selected ? { background: value } : undefined}
        >
            {selected && (
                <CheckIcon
                    className={classNames(styles.swatchCheck, {
                        [styles.swatchCheckOnDark]: isCheckOnDark,
                    })}
                />
            )}
        </button>
    </BackgroundColorPicker>
);
